import { useMemo, useState } from 'react'
import { PlaidItem } from '@/reducers/finances/plaidItemReducer'
import {
  FinancialAccount,
  TransactionSyncState,
} from '@/reducers/finances/financialAccountsReducer'
import {
  selectHiddenAccounts,
  selectVisibleAccounts,
} from '@/selectors/financeSelectors'
import { selectRolledOutInstitutionIds } from '@/selectors/user.selectors'
import { deletePlaidItem } from '@/actions/financialAccountActions'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { checkIsInRolledOutInstitutions } from '../../../helpers'

import { Card, CardColors, Separator, List } from '@/ui'
import { PlaidStatementRelogin } from '../plaid/StatementAccountRelogin'
import { AccountBannerProps } from '../AccountBannerCard'
import { AwaitingConfirmationButton } from '../ConfirmationButton'
import { HiddenAccountsAccordion } from './HiddenAccountsAccordion'
import { HideAccountDialog } from './HideAccountDialog'
import { InstitutionHeader } from './InstitutionHeader'
import { MobileInstitutionHeader } from './MobileHeader'

interface InstitutionCardProps {
  institution: PlaidItem
  setAccountBanner: (accountBanner: AccountBannerProps | null) => void
  setAccountToEdit: (account: FinancialAccount | null) => void
  accountToEdit: FinancialAccount | null
  setInitialStateModalOpen: (account: boolean) => void
  isOnboarding?: boolean
}

export const InstitutionCard = ({
  institution,
  setAccountBanner,
  setAccountToEdit,
  accountToEdit,
  setInitialStateModalOpen,
  isOnboarding = false,
}: InstitutionCardProps) => {
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const [hideModalOpen] = useState(false)
  const { disconnectedOn } = institution
  const rolledOutInstitutionIds = useReselector(selectRolledOutInstitutionIds)
  const isInRolledOutInstitutions = checkIsInRolledOutInstitutions(
    rolledOutInstitutionIds,
    institution.institutionId
  )
  const dispatch = useAppDispatch()

  const hiddenAccounts = useReselector(
    selectHiddenAccounts,
    institution.id,
    isInRolledOutInstitutions
  )
  const visibleAccounts = useReselector(
    selectVisibleAccounts,
    institution.id,
    isInRolledOutInstitutions
  )

  const handleRemoveConnection = async () => {
    await dispatch(deletePlaidItem(institution.id))
  }

  const expandedItem = useMemo(() => {
    if (institution.accounts.length === 0) return ''
    return institution.accounts[0].name
  }, [institution])

  const cardColor = disconnectedOn ? CardColors.DANGER : CardColors.LEVEL_1

  const listTextColor = disconnectedOn
    ? 'text-neutral-700'
    : isInRolledOutInstitutions &&
        accountToEdit?.transactionSyncState === TransactionSyncState.PENDING
      ? 'text-neutral-400'
      : ''

  const enabledRolledOutInstitution =
    isInRolledOutInstitutions &&
    accountToEdit?.transactionSyncState === TransactionSyncState.ENABLED

  const pendingRolledOutInstitution =
    isInRolledOutInstitutions &&
    accountToEdit?.transactionSyncState === TransactionSyncState.PENDING

  return (
    <Card className="institutionCard mt-5" color={cardColor}>
      {isMobile ? (
        <MobileInstitutionHeader
          institution={institution}
          isInRolledOutInstitutions={isInRolledOutInstitutions}
          isOnboarding={isOnboarding}
          handleRemoveConnection={handleRemoveConnection}
        />
      ) : (
        <InstitutionHeader
          institution={institution}
          isInRolledOutInstitutions={isInRolledOutInstitutions}
          isOnboarding={isOnboarding}
          handleRemoveConnection={handleRemoveConnection}
        />
      )}

      <Separator />

      {visibleAccounts.length > 0 && (
        <List className="list-disc ml-2">
          {visibleAccounts.map((account) => (
            <List.Item key={account.id} className={listTextColor}>
              {account.name} {account.mask}
              {enabledRolledOutInstitution && (
                <HideAccountDialog
                  account={accountToEdit}
                  setAccountBanner={setAccountBanner}
                  open={hideModalOpen}
                  onClose={() => {
                    setAccountToEdit(null)
                  }}
                  disconnectedOn={disconnectedOn}
                  setAccountToEdit={setAccountToEdit}
                />
              )}
              {pendingRolledOutInstitution && (
                <AwaitingConfirmationButton
                  account={account}
                  disconnectedOn={disconnectedOn}
                  setAccountToEdit={setAccountToEdit}
                  setInitialStateModalOpen={setInitialStateModalOpen}
                />
              )}
            </List.Item>
          ))}
        </List>
      )}

      {!isOnboarding && (
        <PlaidStatementRelogin
          institution={institution}
          accounts={visibleAccounts}
          disconnected={Boolean(disconnectedOn)}
        />
      )}

      {isInRolledOutInstitutions && hiddenAccounts.length > 0 && (
        <HiddenAccountsAccordion
          hiddenAccounts={hiddenAccounts}
          institutionName={institution.institutionName}
          disconnectedOn={disconnectedOn}
          setAccountBanner={setAccountBanner}
          initialOpen={expandedItem}
        />
      )}
    </Card>
  )
}
